import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { ProtocolTypeTag } from "@msuite/ui/tag";
import { getColorByString } from "@msuite/utils/colors";
import { moment } from "@msuite/utils/date";
import { guardNumber } from "@msuite/utils/numbers";
import { getProtocolTypeTranslationKey } from "@msuite/utils/protocols";
import { guardString } from "@msuite/utils/strings";
import type { Protocol } from "@msuite/utils/types";
import { ProjectCardMention, UserCard } from "@/components/cards";
import { RowFiles } from "@/components/tables";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<Protocol>();

export const columns = [
	columnHelper.accessor((row) => moment(row.createdAt).longFormat(), {
		header: localized("general_fields.created_at"),
		id: "createdAt",
		cell: (cell) => cell.getValue(),
		sortFn: (a, b) => {
			return moment(b.original.createdAt).diff(moment(a.original.createdAt));
		},
	}),
	columnHelper.accessor("title", {
		id: "title",
		header: localized("protocols.title"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.display({
		header: localized("general_fields.created_by"),
		id: "createdBy",
		cell: (cell) => (
			<UserCard
				variant="plain"
				userId={cell.row.original.createdBy}
			/>
		),
	}),
	columnHelper.accessor((row) => localized(getProtocolTypeTranslationKey(row.type)), {
		id: "type",
		header: localized("protocols.type"),
		cell: (cell) => (
			<ProtocolTypeTag
				type={cell.row.original.type}
				variant="plain"
				color={getColorByString(cell.getValue())}
			/>
		),
	}),
	columnHelper.accessor((row) => moment(row.date).dateFormat(), {
		id: "date",
		header: localized("dates.date"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.display({
		id: "context",
		header: localized("protocols.context"),
		cell: ({ row: { original } }) =>
			original.projectId ? (
				<ProjectCardMention projectId={original.projectId} />
			) : original.description ? (
				<p>{original.description}</p>
			) : (
				<p>{guardString(null)}</p>
			),
	}),
	columnHelper.accessor((row) => row._files.length, {
		id: "files",
		header: localized("files.files"),
		cell: (cell) => (
			<RowFiles
				files={cell.row.original._files}
				unlink={{
					tableName: "protocolFiles",
					foreignKey: "protocolId",
					identifier: cell.row.original.id,
				}}
				parent={{ subject: "Protocol", resource: cell.row.original }}
			/>
		),
	}),
	columnHelper.accessor((row) => row._involvements?.length || 0, {
		id: "involvements",
		header: localized("protocols.involvements"),
		cell: (cell) =>
			localized("protocols.count_involvements", { count: `${guardNumber(cell.getValue())}` }),
	}),
	createRowActions(RowActions),
];
